// frontend/src/pages/admin/AssetInventory.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Laptop, Key, Car, FileKey, Package, Plus, Filter,
  ArrowLeftRight, AlertTriangle, CheckCircle, Clock, XCircle,
  Wrench, Users, Building2, Calendar, ChevronDown, ChevronRight,
  RefreshCw, Download, Eye, Edit, Trash2, UserPlus, UserMinus,
  RotateCcw, ShieldOff, Smartphone,
} from 'lucide-react';
import Modal from '../../components/UI/Modal';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import ReturnToInventoryModal from '../../components/asset/ReturnToInventoryModal';
import {
  useAssetDashboard,
  useAssets,
  useAssetCategories,
  useAssetTypes,
  useAvailableAssets,
  useTechRefreshAssets,
  useRecentTransfers,
  usePendingChecklists,
  useCreateAsset,
  useAssignAsset,
  useReturnToInventory,
  useDeactivateAccess,
  useReturnToLessor,
  usePendingReturnsForCustodian,
  useConfirmReturn,
  useRejectReturn,
} from '../../hooks/useAssetInventoryData';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import AssetAssigneeCombobox from '../../components/asset/AssetAssigneeCombobox';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { resolveTransferTypeLabel } from '../../utils/transferTypeMeta';
import { formatAssetTagForDisplay } from '../../utils/mobileLineDisplay';
import { InfoBadge } from '../../components/UI/InfoBadge';
import { deviceLineContext, deviceLineLabel, isDeviceMobileLine, showsRegisterHolder } from '../../utils/mobileLineDisplay';
import { CountBadge } from '../../components/UI/CountBadge';
import AssetDetailModal from '../../components/AssetDetailModal';
import DeactivateAccessModal from '../../components/asset/DeactivateAccessModal';
import ReturnToLessorModal, { ReturnToLessorButton } from '../../components/asset/ReturnToLessorModal';
import {
  canDeactivateAccess,
  canReturnAssetToInventory,
} from '../../utils/assetAccess';
import {
  PendingReturnCard,
  ConfirmReturnModal,
  RejectReturnModal,
} from '../../components/asset/PendingReturnsShared';
import { getTodayInSingaporeTimezone } from '../../utils/dateUtils';

// Category icon component
const CategoryIcon = ({ code, className = "w-5 h-5" }) => {
  const icons = {
    IT: Laptop,
    ACC: Key,
    FLT: Car,
    LIC: FileKey,
    OFF: Package,
    TEL: Smartphone,
  };
  const Icon = icons[code] || Package;
  return <Icon className={className} />;
};

// Dashboard stat card
const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue', onClick }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    red: 'bg-red-50 text-red-600',
    purple: 'bg-purple-50 text-purple-600',
    gray: 'bg-gray-50 text-gray-600',
  };
  
  return (
    <div 
      className={`bg-white rounded-lg border p-4 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-gray-500 truncate">{title}</p>
          <p className="text-lg sm:text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1 truncate">{subtitle}</p>}
        </div>
        <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
      </div>
    </div>
  );
};

const registerHolderNote = (asset) => (
  asset?.register_holder_kind === 'equipment'
    ? 'Site or device'
    : 'Not linked to a staff record'
);

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numberValue = Number(value);
  if (Number.isNaN(numberValue)) return value;
  return numberValue.toLocaleString('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Asset table row
const AssetRow = ({ asset, onView, onAssign, onReturn, onReturnToLessor, onDeactivateAccess }) => {
  const fleet = asset.fleet_vehicle_summary || asset.fleet_vehicle;
  const isFleetAsset = asset.category_code === 'FLT' && fleet;

  return (
    <tr className="table-row">
      <td className="px-4 py-3">
        <div className="flex items-center space-x-3">
          <CategoryIcon code={asset.category_code} className="w-5 h-5 text-gray-400" />
          <div>
            <p className="font-medium text-gray-900 flex items-center gap-1.5">
              <span>{asset.display_name || asset.asset_tag}</span>
              {isDeviceMobileLine(asset) && (
                <InfoBadge color="blue" size="xs" label="Device" title={deviceLineLabel(asset)} />
              )}
            </p>
            <p className="text-sm text-gray-500">{asset.asset_type_name}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm">{asset.brand || '-'}</p>
        <p className="text-xs text-gray-500">{asset.model || '-'}</p>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={asset.status} />
      </td>
      <td className="px-4 py-3">
        {asset.current_assignee_name ? (
          <div>
            <p className="text-sm">{asset.current_assignee_name}</p>
            <p className="text-xs text-gray-500">{asset.current_assignee_staff_id}</p>
            {isDeviceMobileLine(asset) && (
              <p className="text-xs text-blue-700 mt-0.5">{deviceLineLabel(asset)}</p>
            )}
          </div>
        ) : showsRegisterHolder(asset) ? (
          <div>
            <p className="text-sm">{asset.register_holder}</p>
            <p className="text-xs text-gray-500">{registerHolderNote(asset)}</p>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="px-4 py-3">
        {isFleetAsset ? (
          <div className="space-y-1 text-xs text-gray-600">
            <p>
              <span className="font-medium text-gray-700">OMV:</span>{' '}
              {formatCurrency(fleet.open_market_value)}
            </p>
            <p>
              <span className="font-medium text-gray-700">Chassis:</span>{' '}
              {fleet.chassis_number || asset.serial_number || '-'}
            </p>
            <p>
              <span className="font-medium text-gray-700">COE category:</span>{' '}
              {fleet.coe_category_display || fleet.coe_category || '-'}
            </p>
            <p>
              <span className="font-medium text-gray-700">COE price:</span>{' '}
              {formatCurrency(fleet.quota_premium)}
            </p>
          </div>
        ) : (
          <div className="flex items-center space-x-1">
            {asset.needs_tech_refresh && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Refresh</span>
            )}
            {!asset.is_under_warranty && asset.warranty_end_date && (
              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">No Warranty</span>
            )}
            {asset.age_in_years !== null && (
              <span className="text-xs text-gray-500">{asset.age_in_years}y old</span>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <button 
            onClick={() => onView(asset)}
            className="p-1 text-gray-400 hover:text-blue-600"
            title="View Details"
          >
            <Eye className="w-4 h-4" />
          </button>
          {asset.status === 'available' && (
            <button 
              onClick={() => onAssign(asset)}
              className="p-1 text-gray-400 hover:text-green-600"
              title="Assign Asset"
            >
              <UserPlus className="w-4 h-4" />
            </button>
          )}
          {asset.status === 'assigned' && canReturnAssetToInventory(asset) && (
            <button 
              onClick={() => onReturn(asset)}
              className="p-1 text-gray-400 hover:text-orange-600"
              title="Return to Inventory"
            >
              <UserMinus className="w-4 h-4" />
            </button>
          )}
          <ReturnToLessorButton asset={asset} onClick={onReturnToLessor} />
          {canDeactivateAccess(asset) && (
            <button
              onClick={() => onDeactivateAccess(asset)}
              className="p-1 text-gray-400 hover:text-red-600"
              title="Deactivate access"
              aria-label="Deactivate access"
            >
              <ShieldOff className="w-4 h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Mobile card mirror of AssetRow (shown below md in the assets tab)
const AssetMobileCard = ({ asset, onView, onAssign, onReturn, onReturnToLessor, onDeactivateAccess }) => {
  const fleet = asset.fleet_vehicle_summary || asset.fleet_vehicle;
  const isFleetAsset = asset.category_code === 'FLT' && fleet;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
            <span className="truncate">{asset.display_name || asset.asset_tag}</span>
            {isDeviceMobileLine(asset) && (
              <InfoBadge color="blue" size="xs" label="Device" title={deviceLineLabel(asset)} />
            )}
          </p>
          <p className="text-xs text-gray-500 truncate">{asset.asset_type_name}</p>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={asset.status} />
        </div>
      </div>
      <div className="space-y-1.5 flex-1">
        <div className="flex items-start justify-between gap-3 text-sm">
          <span className="text-gray-500 flex-shrink-0">Brand/Model</span>
          <span className="text-gray-900 text-right truncate min-w-0">
            {[asset.brand, asset.model].filter(Boolean).join(' ') || '-'}
          </span>
        </div>
        <div className="flex items-start justify-between gap-3 text-sm">
          <span className="text-gray-500 flex-shrink-0">Assigned To</span>
          <span className="text-gray-900 text-right truncate min-w-0">
            {asset.current_assignee_name
              ? `${asset.current_assignee_name} (${asset.current_assignee_staff_id})`
              : showsRegisterHolder(asset)
                ? `${asset.register_holder} (${registerHolderNote(asset)})`
                : '-'}
          </span>
        </div>
        {isDeviceMobileLine(asset) && (
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-gray-500 flex-shrink-0">Device</span>
            <span className="text-blue-700 text-right min-w-0">
              {deviceLineContext(asset) || 'Site or device'}
            </span>
          </div>
        )}
        {isFleetAsset ? (
          <>
            <div className="flex items-start justify-between gap-3 text-sm">
              <span className="text-gray-500 flex-shrink-0">OMV</span>
              <span className="text-gray-900 text-right truncate min-w-0 tabular-nums font-semibold">
                {formatCurrency(fleet.open_market_value)}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3 text-sm">
              <span className="text-gray-500 flex-shrink-0">Chassis</span>
              <span className="text-gray-900 text-right truncate min-w-0">
                {fleet.chassis_number || asset.serial_number || '-'}
              </span>
            </div>
            <div className="flex items-start justify-between gap-3 text-sm">
              <span className="text-gray-500 flex-shrink-0">COE</span>
              <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">
                {(fleet.coe_category_display || fleet.coe_category || '-')}
                {' · '}
                {formatCurrency(fleet.quota_premium)}
              </span>
            </div>
          </>
        ) : (
          <div className="flex items-start justify-between gap-3 text-sm">
            <span className="text-gray-500 flex-shrink-0">Info</span>
            <span className="text-right min-w-0">
              <span className="inline-flex flex-wrap justify-end items-center gap-1">
                {asset.needs_tech_refresh && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Refresh</span>
                )}
                {!asset.is_under_warranty && asset.warranty_end_date && (
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">No Warranty</span>
                )}
                {asset.age_in_years !== null && (
                  <span className="text-xs text-gray-500">{asset.age_in_years}y old</span>
                )}
                {!asset.needs_tech_refresh && !(!asset.is_under_warranty && asset.warranty_end_date) && asset.age_in_years === null && (
                  <span className="text-gray-400">-</span>
                )}
              </span>
            </span>
          </div>
        )}
      </div>
      <div className="card-action-footer">
        <button
          onClick={() => onView(asset)}
          className="icon-button-admin"
          title="View Details"
          aria-label="View Details"
        >
          <Eye className="w-4 h-4" />
        </button>
        {asset.status === 'available' && (
          <button
            onClick={() => onAssign(asset)}
            className="icon-button-admin text-green-600 hover:text-green-700 hover:bg-green-50"
            title="Assign Asset"
            aria-label="Assign Asset"
          >
            <UserPlus className="w-4 h-4" />
          </button>
        )}
        {asset.status === 'assigned' && canReturnAssetToInventory(asset) && (
          <button
            onClick={() => onReturn(asset)}
            className="icon-button-admin text-orange-600 hover:text-orange-700 hover:bg-orange-50"
            title="Return to Inventory"
            aria-label="Return to Inventory"
          >
            <UserMinus className="w-4 h-4" />
          </button>
        )}
        <ReturnToLessorButton asset={asset} onClick={onReturnToLessor} />
        {canDeactivateAccess(asset) && (
          <button
            onClick={() => onDeactivateAccess(asset)}
            className="icon-button-danger"
            title="Deactivate access"
            aria-label="Deactivate access"
          >
            <ShieldOff className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Checklist item component
const ChecklistCard = ({ checklist }) => {
  const typeColors = {
    onboarding: 'border-green-200 bg-green-50',
    offboarding: 'border-red-200 bg-red-50',
  };
  
  return (
    <div className={`border rounded-lg p-4 ${typeColors[checklist.checklist_type] || 'border-gray-200'}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {checklist.checklist_type === 'onboarding' ? (
            <UserPlus className="w-4 h-4 text-green-600" />
          ) : (
            <UserMinus className="w-4 h-4 text-red-600" />
          )}
          <span className="font-medium">{checklist.employee_name}</span>
        </div>
        <span className="text-xs text-gray-500">{checklist.effective_date}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">
          {checklist.checklist_type_display}
        </span>
        <div className="flex items-center space-x-2">
          <div className="w-24 bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${checklist.checklist_type === 'onboarding' ? 'bg-green-500' : 'bg-red-500'}`}
              style={{ width: `${checklist.completion_percentage}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">{Math.round(checklist.completion_percentage)}%</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        {checklist.pending_items} of {checklist.total_items} items pending
      </p>
    </div>
  );
};

// Transfer item component
const TransferItem = ({ transfer }) => {
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-b-0">
      <div className="flex items-center space-x-3">
        <ArrowLeftRight className="w-4 h-4 text-gray-400" />
        <div>
          <p className="text-sm font-medium whitespace-nowrap">{formatAssetTagForDisplay(transfer.asset_tag)}</p>
          <p className="text-xs text-gray-500">{resolveTransferTypeLabel(transfer)}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm">
          {transfer.from_employee_name || 'Inventory'} → {transfer.to_employee_name || 'Inventory'}
        </p>
        <p className="text-xs text-gray-500">{transfer.effective_date}</p>
      </div>
    </div>
  );
};

// Assign Asset Modal (Modal primitive + design system: input-admin, select-admin, btn-admin-*)
const AssignAssetModal = ({ asset, isOpen, onClose, onConfirm, isLoading }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(getTodayInSingaporeTimezone());
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedEmployee('');
      setEffectiveDate(getTodayInSingaporeTimezone());
      setNotes('');
    }
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    onConfirm({
      asset_id: asset.id,
      employee_id: selectedEmployee,
      effective_date: effectiveDate,
      notes,
    });
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Asset"
      size="sm"
      theme="admin"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline" disabled={isLoading}>
            Cancel
          </button>
          <button
            type="submit"
            form="assign-asset-form"
            className="btn btn-admin-primary"
            disabled={isLoading || !selectedEmployee}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {isLoading ? 'Assigning...' : 'Assign Asset'}
          </button>
        </>
      }
    >
      <form id="assign-asset-form" onSubmit={handleSubmit} className="space-y-4">
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="font-medium text-red-800">{asset.display_name || asset.asset_tag}</p>
                <p className="text-sm text-red-600">{asset.asset_type_name} - {asset.brand} {asset.model}</p>
              </div>
              {asset?.asset_type?.requires_recipient_receipt && (
                <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-2">
                  This asset type requires the employee to acknowledge receipt. The asset will appear in their My Assets after they acknowledge.
                </p>
              )}
              <div>
                <label className="form-label">
                  Assign To <span className="text-red-500">*</span>
                </label>
                <AssetAssigneeCombobox
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  theme="admin"
                  disabled={isLoading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Inactive and offboarded people are not listed — they cannot receive new assets.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="input-admin w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input-admin w-full"
                  rows={2}
                  placeholder="Optional notes..."
                />
              </div>
      </form>
    </Modal>
  );
};

// Main Component
const AssetInventory = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    search: '',
  });
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailAssetId, setDetailAssetId] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [confirmReturnModal, setConfirmReturnModal] = useState({ open: false, transfer: null });
  const [rejectReturnModal, setRejectReturnModal] = useState({ open: false, transfer: null });
  const [deactivateModal, setDeactivateModal] = useState({ open: false, asset: null });
  const [lessorModal, setLessorModal] = useState({ open: false, asset: null });
  const [pendingReturnsSearch, setPendingReturnsSearch] = useState('');
  
  // Data queries
  const { data: dashboard, isLoading: dashboardLoading } = useAssetDashboard();
  const { data: categories } = useAssetCategories({ is_active: true });
  const { data: assetsData, isLoading: assetsLoading } = useAssets({
    status: filters.status || undefined,
    category: filters.category || undefined,
    search: filters.search || undefined,
  });
  const { data: techRefreshAssets } = useTechRefreshAssets();
  const { data: recentTransfers } = useRecentTransfers(10);
  const { data: pendingChecklists } = usePendingChecklists();
  const { data: pendingReturns = [], isLoading: pendingReturnsLoading } = usePendingReturnsForCustodian();
  
  // Mutations
  const assignAsset = useAssignAsset();
  const returnToInventory = useReturnToInventory();
  const deactivateAccess = useDeactivateAccess();
  const returnToLessor = useReturnToLessor();
  const confirmReturn = useConfirmReturn();
  const rejectReturn = useRejectReturn();
  
  // Handlers
  const handleAssign = (asset) => {
    setSelectedAsset(asset);
    setAssignModalOpen(true);
  };
  
  const handleReturn = (asset) => {
    setSelectedAsset(asset);
    setReturnModalOpen(true);
  };

  const handleReturnToLessor = (data) => {
    const asset = lessorModal.asset;
    if (!asset) return;
    returnToLessor.mutate(
      { id: asset.id, data },
      { onSuccess: () => setLessorModal({ open: false, asset: null }) },
    );
  };

  const handleDeactivateAccess = () => {
    const asset = deactivateModal.asset;
    if (!asset) return;
    deactivateAccess.mutate(asset.id, {
      onSuccess: () => setDeactivateModal({ open: false, asset: null }),
    });
  };
  
  const handleAssignConfirm = async (data) => {
    await assignAsset.mutateAsync(data);
    setAssignModalOpen(false);
    setSelectedAsset(null);
  };
  
  const handleReturnConfirm = async (data) => {
    await returnToInventory.mutateAsync(data);
    setReturnModalOpen(false);
    setSelectedAsset(null);
  };

  const handleConfirmReturnReceipt = (data) => {
    if (!confirmReturnModal.transfer) return;
    confirmReturn.mutate(
      {
        transferId: confirmReturnModal.transfer.id,
        location: data.location,
        condition: data.condition,
        photos: data.photos,
      },
      {
        onSuccess: () => setConfirmReturnModal({ open: false, transfer: null }),
      }
    );
  };

  const handleRejectReturnRequest = (data) => {
    if (!rejectReturnModal.transfer) return;
    rejectReturn.mutate(
      {
        transferId: rejectReturnModal.transfer.id,
        reason: data.reason,
      },
      {
        onSuccess: () => setRejectReturnModal({ open: false, transfer: null }),
      }
    );
  };

  // Filter pending returns by search (asset tag, employee name, staff_id)
  const filteredPendingReturns = useMemo(() => {
    if (!pendingReturns?.length) return [];
    const q = pendingReturnsSearch.trim().toLowerCase();
    if (!q) return pendingReturns;
    return pendingReturns.filter((ret) => (
      ret.asset_tag?.toLowerCase().includes(q) ||
      ret.asset_type_name?.toLowerCase().includes(q) ||
      ret.from_employee?.first_name?.toLowerCase().includes(q) ||
      ret.from_employee?.last_name?.toLowerCase().includes(q) ||
      ret.from_employee?.staff_id?.toLowerCase().includes(q)
    ));
  }, [pendingReturns, pendingReturnsSearch]);

  const handleView = (asset) => {
    setDetailAssetId(asset.id);
    setDetailModalOpen(true);
  };
  
  // Tab content
  const renderDashboard = () => {
    if (dashboardLoading) {
      return (
        <div className="space-y-6">
          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-lg border p-4 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-8 bg-gray-200 rounded w-12"></div>
                  </div>
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex-shrink-0"></div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Alerts Row Skeleton */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {[1, 2].map(i => (
              <div key={i} className="bg-white rounded-lg border p-4 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                    <div className="h-8 bg-gray-200 rounded w-12"></div>
                    <div className="h-3 bg-gray-200 rounded w-40"></div>
                  </div>
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex-shrink-0"></div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Category Breakdown Skeleton */}
          <div className="bg-white rounded-lg border p-4 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-40 mb-4"></div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-gray-200 rounded"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-8"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Two Column Layout Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Checklists Skeleton */}
            <div className="bg-white rounded-lg border p-4 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="h-6 bg-gray-200 rounded w-40"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Recent Transfers Skeleton */}
            <div className="bg-white rounded-lg border p-4 animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="h-6 bg-gray-200 rounded w-36"></div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            title="Total Assets"
            value={dashboard?.total_assets || 0}
            icon={Package}
            color="blue"
          />
          <StatCard
            title="Available"
            value={dashboard?.available_count || 0}
            icon={CheckCircle}
            color="green"
            onClick={() => {
              setFilters({ ...filters, status: 'available' });
              setActiveTab('assets');
            }}
          />
          <StatCard
            title="Assigned"
            value={dashboard?.assigned_count || 0}
            icon={Users}
            color="purple"
            onClick={() => {
              setFilters({ ...filters, status: 'assigned' });
              setActiveTab('assets');
            }}
          />
          <StatCard
            title="Pending Disposal"
            value={dashboard?.pending_disposal_count || 0}
            icon={Trash2}
            color="red"
            onClick={() => {
              setFilters({ ...filters, status: 'pending_disposal' });
              setActiveTab('assets');
            }}
          />
        </div>
        
        {/* Alerts Row */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <StatCard
            title="Tech Refresh Needed"
            value={dashboard?.tech_refresh_needed || 0}
            subtitle="Assets exceeding expected lifespan"
            icon={RefreshCw}
            color="yellow"
          />
          <StatCard
            title="Expiring Warranties"
            value={dashboard?.expiring_warranties || 0}
            subtitle="Within 90 days"
            icon={AlertTriangle}
            color="red"
          />
        </div>
        
        {/* Category Breakdown */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold mb-4">Assets by Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {dashboard?.category_breakdown?.map((cat) => (
              <div 
                key={cat.asset_type__category__code}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  // Filter by this category
                  setFilters({ ...filters, category: cat.asset_type__category__code });
                  setActiveTab('assets');
                }}
              >
                <CategoryIcon code={cat.asset_type__category__code} className="w-6 h-6 text-gray-600" />
                <div>
                  <p className="font-medium">{cat.count}</p>
                  <p className="text-xs text-gray-500">{cat.asset_type__category__name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pending Checklists */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h3 className="font-semibold truncate min-w-0">Pending Checklists</h3>
              <span className="text-sm text-gray-500 flex-shrink-0 whitespace-nowrap">
                {pendingChecklists?.length || 0} active
              </span>
            </div>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {pendingChecklists?.length > 0 ? (
                pendingChecklists.map((checklist) => (
                  <ChecklistCard key={checklist.id} checklist={checklist} />
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No pending checklists</p>
              )}
            </div>
          </div>
          
          {/* Recent Transfers */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center justify-between gap-2 mb-4">
              <h3 className="font-semibold truncate min-w-0">Recent Transfers</h3>
              <button 
                onClick={() => setActiveTab('transfers')}
                className="text-sm text-blue-600 hover:underline flex-shrink-0"
              >
                View All
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {recentTransfers?.length > 0 ? (
                recentTransfers.map((transfer) => (
                  <TransferItem key={transfer.id} transfer={transfer} />
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No recent transfers</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const renderAssetList = () => {
    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px] max-w-xl">
              <DebouncedSearchInput
                value={filters.search}
                onSearch={(val) => setFilters(prev => ({ ...prev, search: val }))}
                placeholder="Search assets..."
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="border rounded-lg px-3 py-2"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="assigned">Assigned</option>
              <option value="in_repair">In Repair</option>
              <option value="at_workshop">At Workshop</option>
              <option value="pending_disposal">Pending Disposal</option>
              <option value="disposed">Disposed</option>
              <option value="terminated">Terminated</option>
              <option value="returned_to_lessor">Returned to Lessor</option>
            </select>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="border rounded-lg px-3 py-2"
            >
              <option value="">All Categories</option>
              {categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" />
              <span>Add Asset</span>
            </button>
          </div>
        </div>
        
        {/* Asset Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {assetsLoading ? (
            <div className="p-8">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* Mobile card list (below md) */}
              <div className="md:hidden space-y-3 p-4">
                {assetsData?.results?.map((asset) => (
                  <AssetMobileCard
                    key={asset.id}
                    asset={asset}
                    onView={handleView}
                    onAssign={handleAssign}
                    onReturn={handleReturn}
                    onReturnToLessor={(a) => setLessorModal({ open: true, asset: a })}
                    onDeactivateAccess={(a) => setDeactivateModal({ open: true, asset: a })}
                  />
                ))}
              </div>
              {/* Desktop table (md and up) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Asset</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Brand/Model</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Assigned To</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Info</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {assetsData?.results?.map((asset) => (
                      <AssetRow 
                        key={asset.id} 
                        asset={asset}
                        onView={handleView}
                        onAssign={handleAssign}
                        onReturn={handleReturn}
                        onReturnToLessor={(a) => setLessorModal({ open: true, asset: a })}
                        onDeactivateAccess={(a) => setDeactivateModal({ open: true, asset: a })}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Shared empty state (both views) */}
              {assetsData?.results?.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  No assets found matching your criteria
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };
  
  const renderChecklists = () => {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold mb-4">Onboarding & Offboarding Checklists</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingChecklists?.map((checklist) => (
              <ChecklistCard key={checklist.id} checklist={checklist} />
            ))}
          </div>
          {(!pendingChecklists || pendingChecklists.length === 0) && (
            <p className="text-gray-500 text-center py-8">No pending checklists</p>
          )}
        </div>
      </div>
    );
  };
  
  const renderTransfers = () => {
    return (
      <div className="bg-white rounded-lg border p-4">
        <h3 className="font-semibold mb-4">Transfer History</h3>
        <div className="space-y-2">
          {recentTransfers?.map((transfer) => (
            <TransferItem key={transfer.id} transfer={transfer} />
          ))}
        </div>
        {(!recentTransfers || recentTransfers.length === 0) && (
          <p className="text-gray-500 text-center py-8">No transfers recorded</p>
        )}
      </div>
    );
  };

  const renderPendingReturns = () => {
    const expiringSoonCount = pendingReturns.filter((r) => {
      if (!r.expires_at) return false;
      return (new Date(r.expires_at) - new Date()) < 24 * 60 * 60 * 1000;
    }).length;
    const faultyDamagedCount = pendingReturns.filter((r) => ['faulty', 'damaged'].includes(r.return_reason)).length;

    return (
      <div className="space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          <StatCard
            title="Total Pending"
            value={pendingReturns.length}
            icon={RotateCcw}
            color="yellow"
          />
          <StatCard
            title="Faulty / Damaged"
            value={faultyDamagedCount}
            subtitle="Will be sent to repair on confirm"
            icon={AlertTriangle}
            color="red"
          />
          <StatCard
            title="Expiring Soon"
            value={expiringSoonCount}
            subtitle="< 24 hours"
            icon={Clock}
            color="yellow"
          />
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg border p-4">
          <DebouncedSearchInput
            value={pendingReturnsSearch}
            onSearch={setPendingReturnsSearch}
            placeholder="Search by asset tag, type, or employee..."
          />
        </div>

        {/* Cards */}
        <div className="bg-white rounded-lg border p-4">
          {pendingReturnsLoading ? (
            <div className="p-8">
              <LoadingSpinner />
            </div>
          ) : filteredPendingReturns.length === 0 ? (
            <div className="text-center py-12">
              <RotateCcw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {pendingReturnsSearch ? 'No returns match your search' : 'No pending return requests'}
              </p>
              {!pendingReturnsSearch && (
                <p className="text-xs text-gray-400 mt-1">
                  Returns initiated by employees will appear here for your acknowledgement
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPendingReturns.map((ret) => (
                <PendingReturnCard
                  key={ret.id}
                  returnTransfer={ret}
                  onConfirm={(t) => setConfirmReturnModal({ open: true, transfer: t })}
                  onReject={(t) => setRejectReturnModal({ open: true, transfer: t })}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Asset Inventory</h1>
          <p className="text-xs sm:text-sm text-gray-500">Track and manage company assets, transfers, and checklists</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/external-tokens?tab=service-keys&search=asset')}
          className="btn btn-admin-outline w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-shrink-0"
          title="Manage Service API Keys for asset & fleet data"
          aria-label="API Access"
        >
          <Key className="w-4 h-4" />
          <span className="hidden sm:inline">API Access</span>
        </button>
      </div>
      
      {/* Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Building2 },
          { id: 'assets', label: 'Assets', icon: Package },
          { id: 'pending_returns', label: 'Pending Returns', icon: RotateCcw, badge: pendingReturns.length },
          { id: 'checklists', label: 'Checklists', icon: CheckCircle },
          { id: 'transfers', label: 'Transfers', icon: ArrowLeftRight },
        ].map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              {tab.badge > 0 && (
                <CountBadge count={tab.badge} theme="orange" active={isActive} className="ml-1" />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Tab Content */}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'assets' && renderAssetList()}
      {activeTab === 'pending_returns' && renderPendingReturns()}
      {activeTab === 'checklists' && renderChecklists()}
      {activeTab === 'transfers' && renderTransfers()}
      
      {/* Modals */}
      {selectedAsset && (
        <>
          <AssignAssetModal
            asset={selectedAsset}
            isOpen={assignModalOpen}
            onClose={() => {
              setAssignModalOpen(false);
              setSelectedAsset(null);
            }}
            onConfirm={handleAssignConfirm}
            isLoading={assignAsset.isPending}
          />
          <ReturnToInventoryModal
            asset={selectedAsset}
            isOpen={returnModalOpen}
            onClose={() => {
              setReturnModalOpen(false);
              setSelectedAsset(null);
            }}
            onSubmit={handleReturnConfirm}
            isLoading={returnToInventory.isPending}
            title="Return Asset to Inventory"
            submitLabel="Return Asset"
            submittingLabel="Returning..."
          />
        </>
      )}

      <DeactivateAccessModal
        isOpen={deactivateModal.open}
        onClose={() => setDeactivateModal({ open: false, asset: null })}
        asset={deactivateModal.asset}
        onConfirm={handleDeactivateAccess}
        isLoading={deactivateAccess.isPending}
      />

      <ReturnToLessorModal
        isOpen={lessorModal.open}
        onClose={() => setLessorModal({ open: false, asset: null })}
        asset={lessorModal.asset}
        onSubmit={handleReturnToLessor}
        isLoading={returnToLessor.isPending}
      />

      {/* Pending-return modals (custodian acknowledges employee-initiated returns) */}
      <ConfirmReturnModal
        key={confirmReturnModal.transfer?.id ?? 'confirm-closed'}
        isOpen={confirmReturnModal.open}
        onClose={() => setConfirmReturnModal({ open: false, transfer: null })}
        returnTransfer={confirmReturnModal.transfer}
        onConfirm={handleConfirmReturnReceipt}
        isLoading={confirmReturn.isPending}
      />
      <RejectReturnModal
        key={rejectReturnModal.transfer?.id ?? 'reject-closed'}
        isOpen={rejectReturnModal.open}
        onClose={() => setRejectReturnModal({ open: false, transfer: null })}
        returnTransfer={rejectReturnModal.transfer}
        onReject={handleRejectReturnRequest}
        isLoading={rejectReturn.isPending}
      />
      <AssetDetailModal
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setDetailAssetId(null);
        }}
        assetId={detailAssetId}
      />
    </div>
  );
};

export default AssetInventory;
